import type { ClientGPSLocation } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { IconButton, Searchbar } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ArrowBackIcon, ArrowInsertIcon, CloseIcon } from '@/assets';
import { MAX_SEARCH_LENGTH } from '@/constants/search';
import { useReverse } from '@/features/geo-coding/hooks/useReverse';
import { useSearch } from '@/features/geo-coding/hooks/useSearch';
import type { OsmPlace } from '@/features/geo-coding/types/place';
import { getLocationString } from '@/features/geo-coding/utils/places';
import { ListItem } from '@/features/list/components/ListItem';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedModal } from '@/shared/components/ExtendedModal';
import { CurrentLocationListItem } from '@/shared/components/location/CurrentLocationListItem';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { getCurrentLocation } from '@/shared/utils/location';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type LocationSearchModalProps = {
  showSearchModal: boolean;
  placeName?: string;
  setShowSearchModal: Dispatch<SetStateAction<boolean>>;
  onSelect: (position: ClientGPSLocation) => void;
};

export const LocationSearchModal: FC<LocationSearchModalProps> = ({
  showSearchModal,
  placeName = '',
  setShowSearchModal,
  onSelect
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'screens.post-editor.locationPicker'
  });

  const [search, setSearch] = useState<string>('');
  const [searchGeoCodePosition, setSearchGeoCodePosition] = useState<
    ClientGPSLocation | undefined
  >();

  const handleGetCurrentLocation = useCallback(async () => {
    const location = await getCurrentLocation();
    setSearchGeoCodePosition(location);
  }, []);

  useEffect(() => {
    if (isEmptyOrNull(placeName)) return;
    setSearch(placeName);
  }, [placeName]);

  const { data: searchResults, isLoading: isSearchLoading } = useSearch(search);

  const uniqueResults = useMemo<OsmPlace[]>(
    () => [
      ...new Map<string, OsmPlace>(
        searchResults
          ?.map((place) => ({
            ...place,
            display_name: getLocationString(place)
          }))
          .map((place) => [place.display_name, place])
      ).values()
    ],
    [searchResults]
  );

  const handleSearchResultSelected = useCallback(
    (place: OsmPlace) => {
      setShowSearchModal(false);
      setSearch('');
      onSelect({ latitude: place.lat, longitude: place.lon });
    },
    [onSelect, setShowSearchModal]
  );

  const { data: searchReverseResponse } = useReverse(
    searchGeoCodePosition?.latitude,
    searchGeoCodePosition?.longitude
  );

  useEffect(() => {
    if (searchReverseResponse) {
      setSearch(getLocationString(searchReverseResponse));
    }
  }, [searchReverseResponse]);

  const { top } = useSafeAreaInsets();

  return (
    <ExtendedModal
      visible={showSearchModal}
      onDismiss={() => setShowSearchModal(false)}
      onRequestClose={() => setShowSearchModal(false)}
      statusBarTranslucent
      animationType="fade"
    >
      <View
        style={{
          flex: 1,
          paddingTop: top,
          backgroundColor: theme.colors.surfaceContainerLow
        }}
      >
        <Searchbar
          // oxlint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          style={{ backgroundColor: theme.colors.surfaceContainerLow }}
          mode="view"
          placeholder={t('locationSearchPlaceholder')}
          value={search}
          maxLength={MAX_SEARCH_LENGTH}
          onChangeText={setSearch}
          loading={isSearchLoading}
          onIconPress={() => setShowSearchModal(false)}
          icon={({ color, size }) => (
            <ArrowBackIcon fill={color} width={size} height={size} />
          )}
          clearIcon={({ color, size }) =>
            !isEmptyOrNull(search) && (
              <CloseIcon fill={color} width={size} height={size} />
            )
          }
          onClearIconPress={() => {
            setSearch('');
            setSearchGeoCodePosition(undefined);
          }}
        />
        {isEmptyOrNull(search) && (
          <CurrentLocationListItem
            onPress={handleGetCurrentLocation}
            title={t('locationSearchCurrentLocation')}
          />
        )}
        <KeyboardAwareScrollView keyboardShouldPersistTaps="handled">
          <StateView
            stateLabels={t('state', { returnObjects: true })}
            backgroundColor={theme.colors.surfaceContainerLow}
            contentContainerStyle={{ paddingBottom: 32 }}
            isLoading={isSearchLoading}
            isEmpty={search.length > 3 && !hasItems(uniqueResults)}
            centerIndicators={false}
          >
            {hasItems(uniqueResults) &&
              uniqueResults.map((place) => (
                <ListItem
                  key={place.osm_id}
                  title={place.display_name}
                  arrowIcon={() => (
                    <IconButton
                      style={{ margin: -8 }}
                      onPress={() => setSearch(place.display_name ?? '')}
                      icon={({ color, size }) => (
                        <ArrowInsertIcon
                          fill={color}
                          width={size}
                          height={size}
                        />
                      )}
                    />
                  )}
                  onPress={() => handleSearchResultSelected(place)}
                />
              ))}
          </StateView>
        </KeyboardAwareScrollView>
      </View>
    </ExtendedModal>
  );
};
